/**
 * The calculator in reverse: the gross salary that leaves a given net, under the same options
 * (municipality, church, deductions...). Net pay is a monotonic function of gross, so a
 * bisection converges in a few dozen steps; the answer is rounded to whole euros.
 */
import { calculateTaxIceberg, type TaxIcebergInput } from './calculate-tax-iceberg';

export type SolveInputs = Omit<TaxIcebergInput, 'grossAnnual'>;

export function netForGross(grossAnnual: number, inputs: SolveInputs): number {
  if (!(grossAnnual > 0)) return 0;
  return calculateTaxIceberg({ ...inputs, grossAnnual }).salary.net;
}

export function solveGrossForNet(targetNetAnnual: number, inputs: SolveInputs): number {
  if (!(targetNetAnnual > 0) || !Number.isFinite(targetNetAnnual)) return 0;
  let lo = 0;
  let hi = Math.max(1000, targetNetAnnual * 1.3);
  let guard = 0;
  while (netForGross(hi, inputs) < targetNetAnnual && guard++ < 40) hi *= 2;
  for (let i = 0; i < 80 && hi - lo > 0.25; i++) {
    const mid = (lo + hi) / 2;
    if (netForGross(mid, inputs) < targetNetAnnual) lo = mid;
    else hi = mid;
  }
  return Math.round(hi);
}

/** What a raise leaves in hand: net before and after, the share of the raise kept, and what the
 *  raise costs the employer. All annual; the caller converts to the period shown. */
export function raiseOutcome(inputs: TaxIcebergInput, raiseAnnual: number): {
  grossBefore: number;
  grossAfter: number;
  netBefore: number;
  netAfter: number;
  netGain: number;
  keptPct: number;
  marginalRatePct: number;
  employerCostIncrease: number;
} {
  const before = calculateTaxIceberg(inputs);
  const after = calculateTaxIceberg({ ...inputs, grossAnnual: inputs.grossAnnual + raiseAnnual });
  const netGain = after.salary.net - before.salary.net;
  const keptPct = raiseAnnual > 0 ? (netGain / raiseAnnual) * 100 : 0;
  return {
    grossBefore: inputs.grossAnnual,
    grossAfter: inputs.grossAnnual + raiseAnnual,
    netBefore: before.salary.net,
    netAfter: after.salary.net,
    netGain,
    keptPct,
    marginalRatePct: 100 - keptPct,
    employerCostIncrease: after.salary.trueEmployerCost - before.salary.trueEmployerCost,
  };
}
